import type { Album, Track } from 'src/types';
import i18n from '../i18n';

// Minimal shape for contributor name fields we may encounter from various sources
type ContributorLike =
  | {
      givenName?: string;
      name?: string;
      fullName?: string;
    }
  | null
  | undefined;

// Playback mode values used in analytics and player logic
export type PlaybackMode = 'single' | 'album' | 'playlist' | 'shuffle';

// Helper to extract contributor name from various field combinations
const extractContributorName = (c: ContributorLike): string =>
  c?.givenName || c?.name || c?.fullName || '';

// Derive playback mode based on shuffle state and album metadata
export const getPlaybackMode = (args: {
  shuffleEnabled: boolean;
  album?: Pick<Album, 'type' | 'tracks'> | null;
}): PlaybackMode => {
  const { shuffleEnabled, album } = args;

  if (shuffleEnabled) return 'shuffle';

  const type = album?.type?.toLowerCase();
  if (type === 'playlist') return 'playlist';

  const tracksCount = album?.tracks?.length ?? 0;
  if (tracksCount > 1) return 'album';

  return 'single';
};

// Derive creator display name for analytics and UI
export const getCreatorName = (args: {
  album?: Pick<Album, 'category' | 'contributors' | 'creator'> | null;
  track?: Pick<Track, 'contributors'> | null;
}): string => {
  const { album, track } = args;
  // Prefer album creator for user-generated content
  if (album?.category?.toLowerCase() === 'my-creations') {
    return album?.creator?.fullName || i18n.t('common.unknownArtist');
  }
  // Otherwise try track contributor, then album contributor
  const firstTrackContributor = track?.contributors?.[0];
  const firstAlbumContributor = album?.contributors?.[0];

  const trackContributorName = extractContributorName(firstTrackContributor);
  const albumContributorName = extractContributorName(firstAlbumContributor);

  return trackContributorName || albumContributorName || i18n.t('common.unknownArtist');
};
